/**
 * DentalIndicesPanel — Sub-pestaña "Indices Clinicos" del Odontograma.
 *
 * Muestra el calculo Löe & Silness (PI/GI/PPD/BOP) con separacion automatica
 * Natural vs Grupos de Implantes. Incluye tabla de promedios del estudio +
 * desglose por voluntario. Exportacion a Excel.
 */
import { useEffect, useState, useCallback, Fragment } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { DownloadSimple, ChartBar } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { naturalVCodeCmp } from '../../utils/sortVolunteers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INDEX_LABELS = {
  pi:  { code: 'PI',  pct: '%PI',  color: '#1f3e80' },
  gi:  { code: 'GI',  pct: '%GI',  color: '#059669' },
  ppd: { code: 'PPD', pct: '%PPD', color: '#7c3aed' },
  bop: { code: 'BOP', pct: '%BOP', color: '#dc2626' },
};

function fmt(v) {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return v.toFixed(2);
  return v;
}

export default function DentalIndicesPanel({ studyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [expandedVol, setExpandedVol] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/studies/${studyId}/dental-indices`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error(errText(e, 'Error cargando indices'));
    } finally {
      setLoading(false);
    }
  }, [studyId]);

  useEffect(() => { load(); }, [load]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/studies/${studyId}/dental-indices/export.xlsx`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `indices_dentales_${data?.study_code || studyId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Excel descargado');
    } catch (e) {
      toast.error(errText(e, 'Error exportando'));
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-stone-500">Calculando indices...</div>;
  if (!data) return null;

  if (data.no_index_config) {
    return (
      <div className="p-6 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
        <p className="font-semibold mb-1">No hay tipos de indice configurados</p>
        <p>{data.message}</p>
      </div>
    );
  }

  const indexMeas = data.index_measurements || [];
  const activeIndices = indexMeas.map(m => m.index_type);

  return (
    <div className="space-y-6" data-testid="dental-indices-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ChartBar size={22} className="text-[#1f3e80]" />
          <h3 className="text-base font-semibold text-stone-800">Indices Clinicos (Löe &amp; Silness)</h3>
        </div>
        <Button onClick={exportExcel} disabled={exporting} data-testid="dental-indices-export-xlsx">
          <DownloadSimple size={16} className="mr-1" />
          {exporting ? 'Exportando...' : 'Exportar Excel'}
        </Button>
      </div>

      {/* Info panel: indices activos */}
      <div className="p-3 bg-stone-50 border border-stone-200 rounded-md">
        <p className="text-xs text-stone-600 mb-2">Indices calculados en este estudio:</p>
        <div className="flex flex-wrap gap-2">
          {indexMeas.map(m => (
            <span
              key={m.name}
              className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-stone-300 rounded text-xs font-medium"
              style={{ borderColor: INDEX_LABELS[m.index_type]?.color }}
            >
              <span style={{ color: INDEX_LABELS[m.index_type]?.color }} className="font-bold">
                {INDEX_LABELS[m.index_type]?.code}
              </span>
              <span className="text-stone-500">— {m.label}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Study Averages Table */}
      <div>
        <h4 className="text-sm font-semibold text-stone-800 mb-2">Promedios del estudio por visita</h4>
        <div className="overflow-x-auto border border-stone-200 rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-[#1f3e80] text-white">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Visita</th>
                <th className="px-3 py-2 text-center font-medium">Total vol.</th>
                {activeIndices.map(idx => (
                  <th key={idx} colSpan={4} className="px-3 py-2 text-center font-medium border-l border-white/20">
                    {INDEX_LABELS[idx]?.code}
                  </th>
                ))}
              </tr>
              <tr className="text-xs">
                <th></th>
                <th></th>
                {activeIndices.map(idx => (
                  <Fragment key={idx}>
                    <th className="px-2 py-1 bg-emerald-700 text-white font-normal border-l border-white/20">Nat. medio</th>
                    <th className="px-2 py-1 bg-emerald-700 text-white font-normal">Nat. %</th>
                    <th className="px-2 py-1 bg-orange-700 text-white font-normal">Imp. medio</th>
                    <th className="px-2 py-1 bg-orange-700 text-white font-normal">Imp. %</th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data.study_averages || []).map(row => (
                <tr key={row.visit_id} className="border-t border-stone-200">
                  <td className="px-3 py-2 font-mono">{row.visit_code} — {row.visit_name}</td>
                  <td className="px-3 py-2 text-center">{row.n_volunteers}</td>
                  {activeIndices.map(idx => {
                    const st = row.indices?.[idx] || {};
                    return (
                      <Fragment key={idx}>
                        <td className="px-2 py-2 text-center bg-emerald-50 border-l border-stone-200">{fmt(st.nat_mean)}</td>
                        <td className="px-2 py-2 text-center bg-emerald-50">{fmt(st.nat_pct)}%</td>
                        <td className="px-2 py-2 text-center bg-orange-50">{fmt(st.imp_mean)}</td>
                        <td className="px-2 py-2 text-center bg-orange-50">{fmt(st.imp_pct)}%</td>
                      </Fragment>
                    );
                  })}
                </tr>
              ))}
              {(data.study_averages || []).every(r => r.n_volunteers === 0) && (
                <tr><td colSpan={2 + activeIndices.length * 4} className="px-3 py-6 text-center text-stone-500 italic">Sin datos registrados aun</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-volunteer breakdown */}
      <div>
        <h4 className="text-sm font-semibold text-stone-800 mb-2">Resultados por voluntario</h4>
        <div className="space-y-2">
          {/* URS-F506: orden natural por V-code (V1, V2, ..., V10, V11)
              en vez de lexicografico. */}
          {[...(data.results || [])].sort(naturalVCodeCmp).map(vol => (
            <div key={vol.volunteer_id} className="border border-stone-200 rounded-md bg-white">
              <button
                type="button"
                onClick={() => setExpandedVol(expandedVol === vol.volunteer_id ? null : vol.volunteer_id)}
                className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-stone-50"
                data-testid={`dental-vol-toggle-${vol.volunteer_id}`}
              >
                <span className="font-semibold text-sm text-stone-800">
                  {vol.study_volunteer_code || vol.volunteer_id}
                  <span className="ml-2 text-xs text-stone-500 uppercase">({vol.volunteer_status})</span>
                </span>
                <span className="text-xs text-stone-500">
                  {vol.visits.length} visita{vol.visits.length !== 1 ? 's' : ''} con datos
                  <span className="ml-2">{expandedVol === vol.volunteer_id ? '▼' : '▶'}</span>
                </span>
              </button>
              {expandedVol === vol.volunteer_id && (
                <div className="px-3 py-3 border-t border-stone-200 space-y-3">
                  {vol.visits.map(v => (
                    <div key={v.visit_id}>
                      <p className="text-xs font-semibold text-stone-700 uppercase tracking-wide mb-2">
                        {v.visit_code} — {v.visit_name}
                      </p>
                      <div className="space-y-2">
                        {Object.entries(v.by_index).map(([idx, dat]) => (
                          <div key={idx} className="border border-stone-200 rounded p-2 text-xs">
                            <div className="font-semibold mb-1" style={{ color: INDEX_LABELS[idx]?.color }}>
                              {dat.code} — {dat.label}
                            </div>
                            <table className="w-full">
                              <thead className="text-stone-600">
                                <tr>
                                  <th className="text-left px-2">Grupo</th>
                                  <th className="text-left px-2">Dientes</th>
                                  <th className="text-center px-2">N sup.</th>
                                  <th className="text-center px-2">Suma</th>
                                  <th className="text-center px-2">Media</th>
                                  <th className="text-center px-2">Pres.</th>
                                  <th className="text-center px-2">%</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr className="bg-emerald-50">
                                  <td className="px-2 py-1 font-medium">Natural</td>
                                  <td className="px-2 py-1 font-mono text-[10px]">{v.natural_teeth.join(', ') || '—'}</td>
                                  <td className="text-center">{dat.natural.count_surfaces}</td>
                                  <td className="text-center">{fmt(dat.natural.sum)}</td>
                                  <td className="text-center font-semibold">{fmt(dat.natural.mean)}</td>
                                  <td className="text-center">{dat.natural.presence}</td>
                                  <td className="text-center font-semibold">{fmt(dat.natural.pct)}%</td>
                                </tr>
                                {dat.implants.map(g => (
                                  <tr key={g.group_id} className="bg-orange-50">
                                    <td className="px-2 py-1 font-medium">Implante G{g.group_id} ({g.arch})</td>
                                    <td className="px-2 py-1 font-mono text-[10px]">{g.teeth.join(', ')}</td>
                                    <td className="text-center">{g.stats.count_surfaces}</td>
                                    <td className="text-center">{fmt(g.stats.sum)}</td>
                                    <td className="text-center font-semibold">{fmt(g.stats.mean)}</td>
                                    <td className="text-center">{g.stats.presence}</td>
                                    <td className="text-center font-semibold">{fmt(g.stats.pct)}%</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {(data.results || []).length === 0 && (
            <p className="text-sm text-stone-500 italic p-3">Sin mediciones registradas aun. Introduce datos en la pestaña &quot;Mediciones&quot;.</p>
          )}
        </div>
      </div>
    </div>
  );
}
